import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Gift, BellRing, Truck } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

const Input = ({ id, label, ...rest }) => (
  <label className="block text-sm">
    <span className="font-semibold text-slate-700">{label}</span>
    <input data-testid={id} {...rest} className="mt-1 w-full h-11 border border-slate-300 rounded-md px-3 outline-none focus:border-[#172554] focus:ring-2 focus:ring-[#172554]/15" />
  </label>
);

export default function AuthPage() {
  const [params] = useSearchParams();
  const { signIn } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState(params.get("tab") === "kayit" ? "kayit" : "giris");
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ name: "", email: "", phone: "", password: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = (path) => async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = path === "login" ? { email: f.email, password: f.password } : f;
      const { data } = await api.post(`/auth/${path}`, body);
      signIn(data);
      toast.success(`Hoş geldiniz, ${data.user.name}`);
      nav(params.get("next") || (data.user.role === "admin" ? "/yonetim" : "/"));
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const Btn = ({ children, id }) => (
    <button data-testid={id} disabled={busy} className="w-full h-12 rounded-md bg-[#172554] hover:bg-[#1E3A8A] text-white font-bold flex items-center justify-center gap-2 disabled:opacity-60">
      {busy && <Loader2 className="h-4 w-4 animate-spin" />}{children}
    </button>
  );

  return (
    <div className="max-w-5xl mx-auto px-4 py-12 grid md:grid-cols-2 gap-8 items-start">
      <div className="hidden md:block">
        <h1 className="text-4xl lg:text-5xl font-extrabold text-[#172554] leading-tight">Üye olun, kampanyalardan ilk siz haberdar olun.</h1>
        <div className="mt-8 space-y-4 text-slate-700">
          {[[Gift, "Üyelere özel indirimler"], [BellRing, "Maması bitmeden hatırlatma"], [Truck, "Siparişlerinizi kolayca takip edin"]].map(([I, t]) => (
            <div key={t} className="flex items-center gap-3"><span className="h-10 w-10 rounded-md bg-green-50 text-[#16A34A] flex items-center justify-center"><I className="h-5 w-5" /></span>{t}</div>
          ))}
        </div>
      </div>
      <div className="bg-white border rounded-xl p-6 sm:p-8">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-2 w-full">
            <TabsTrigger value="giris" data-testid="auth-tab-login">Giriş Yap</TabsTrigger>
            <TabsTrigger value="kayit" data-testid="auth-tab-register">Kayıt Ol</TabsTrigger>
          </TabsList>
          <TabsContent value="giris">
            <form onSubmit={submit("login")} className="space-y-4 mt-4">
              <Input id="login-email" label="E-posta, telefon veya kullanıcı adı" type="text" required value={f.email} onChange={set("email")} placeholder="ornek@mail.com, 05xx... veya kullanıcı adı" />
              <Input id="login-password" label="Şifre" type="password" required value={f.password} onChange={set("password")} />
              <Btn id="login-submit-button">Giriş Yap</Btn>
            </form>
          </TabsContent>
          <TabsContent value="kayit">
            <form onSubmit={submit("register")} className="space-y-4 mt-4">
              <Input id="register-name" label="Ad Soyad" required value={f.name} onChange={set("name")} />
              <Input id="register-email" label="E-posta" type="email" required value={f.email} onChange={set("email")} />
              <Input id="register-phone" label="Cep telefonu" type="tel" required minLength={10} placeholder="05xx xxx xx xx" value={f.phone} onChange={set("phone")} />
              <Input id="register-password" label="Şifre (en az 6 karakter)" type="password" required minLength={6} value={f.password} onChange={set("password")} />
              <Btn id="register-submit-button">Üye Ol</Btn>
            </form>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
